import type { TableGroupByOption } from "@/components/table";
import { formatLoadType } from "@/lib/containerLines";
import { formatPersonName } from "@/lib/formatPersonName";
import { formatRouteLabel } from "@/lib/formatPort";
import type { BookingGroupRow, BookingsFilters } from "@/types/api";
import { BOOKING_STATE_LABELS, type BookingState } from "@/types/booking";

export const BOOKING_GROUP_BY_OPTIONS: TableGroupByOption[] = [
  { value: "", label: "No grouping" },
  { value: "route", label: "Route" },
  { value: "service_trade", label: "Service trade" },
  { value: "week", label: "Week" },
  { value: "carrier", label: "Carrier" },
  { value: "load_type", label: "Load type" },
  { value: "state", label: "State" },
  { value: "bl_group", label: "BL group" },
  { value: "customer", label: "Consignee" },
  { value: "agent", label: "Agent" },
  { value: "sales_person", label: "Sales" },
  { value: "opened_by", label: "Opened by" },
  { value: "open_date", label: "Opened date" },
  { value: "ready_date", label: "Ready date" },
  { value: "stuffing_date", label: "Stuffing date" },
  { value: "cutoff_date", label: "Cutoff date" },
  { value: "etd_date", label: "Departure date" },
];

export const BOOKING_GROUP_BY_VALUES = BOOKING_GROUP_BY_OPTIONS.map(
  (option) => option.value,
);

/** Group bucket label from the API/MV when a date dimension is unset. */
export const BOOKING_GROUP_NO_DATE_KEY = "No date";

export function bookingGroupLabel(
  group: BookingGroupRow,
  groupBy: string,
  showLocodes: boolean,
): string {
  if (groupBy === "route") {
    return formatRouteLabel(
      { locode: group.group_key_1, name: group.group_name_1 },
      { locode: group.group_key_2 ?? "", name: group.group_name_2 },
      showLocodes,
    );
  }

  if (groupBy === "state") {
    return (
      BOOKING_STATE_LABELS[group.group_key_1 as BookingState] ??
      group.group_label ??
      group.group_key_1
    );
  }

  if (groupBy === "load_type") {
    return (
      formatLoadType(group.group_key_1) ??
      group.group_label ??
      group.group_key_1
    );
  }

  if (
    groupBy === "agent" ||
    groupBy === "customer" ||
    groupBy === "sales_person" ||
    groupBy === "opened_by"
  ) {
    return formatPersonName(group.group_label || group.group_key_1);
  }

  return group.group_label || group.group_key_1 || "—";
}

export function bookingGroupChildFilters(
  group: BookingGroupRow,
  groupBy: string,
  base: BookingsFilters,
): BookingsFilters {
  const filters: BookingsFilters = { ...base, page: 1, per_page: 100 };

  if (groupBy === "route") {
    filters.origin_port = group.group_key_1;
    filters.destination_port = group.group_key_2 ?? "";
  } else if (groupBy === "carrier") {
    filters.carrier = group.group_key_1;
  } else if (groupBy === "load_type") {
    filters.load_type = group.group_key_1;
  } else if (groupBy === "state") {
    filters.state = group.group_key_1;
  } else if (groupBy === "week") {
    filters.week = group.group_key_1;
  } else if (groupBy === "customer") {
    filters.consignee = group.group_key_1;
  } else if (groupBy === "agent") {
    filters.agent = group.group_key_1;
  } else if (groupBy === "sales_person") {
    filters.sales_person = group.group_key_1;
  } else if (groupBy === "opened_by") {
    filters.opened_by = group.group_key_1;
  } else if (groupBy === "service_trade") {
    filters.service_trade = group.group_key_1;
  } else if (groupBy === "bl_group") {
    filters.bl_group_id = group.group_key_1;
  } else if (
    groupBy === "stuffing_date" ||
    groupBy === "ready_date" ||
    groupBy === "cutoff_date" ||
    groupBy === "etd_date" ||
    groupBy === "open_date"
  ) {
    // Group key is the ISO date (or "No date"); only real dates map to a
    // one-day range filter.
    const isoDate = /^\d{4}-\d{2}-\d{2}$/.test(group.group_key_1)
      ? group.group_key_1
      : null;
    if (isoDate) {
      if (groupBy === "stuffing_date") {
        filters.stuffing_from = isoDate;
        filters.stuffing_to = isoDate;
      } else if (groupBy === "ready_date") {
        filters.ready_date_from = isoDate;
        filters.ready_date_to = isoDate;
      } else if (groupBy === "cutoff_date") {
        filters.cutoff_from = isoDate;
        filters.cutoff_to = isoDate;
      } else if (groupBy === "open_date") {
        filters.open_date_from = isoDate;
        filters.open_date_to = isoDate;
      } else {
        filters.departure_from = isoDate;
        filters.departure_to = isoDate;
      }
    } else if (group.group_key_1 === BOOKING_GROUP_NO_DATE_KEY) {
      if (groupBy === "stuffing_date") {
        filters.stuffing_missing = "true";
      } else if (groupBy === "ready_date") {
        filters.ready_date_missing = "true";
      } else if (groupBy === "cutoff_date") {
        filters.cutoff_missing = "true";
      } else if (groupBy === "open_date") {
        filters.open_date_missing = "true";
      } else {
        filters.departure_missing = "true";
      }
    }
  }

  return filters;
}
